import type { SearchParams } from '@features/partnerships/context/partnerships-wizard-types';

/** The parts a search record needs to derive its auto-title. */
export interface SearchTitleInput {
  city?: string | null;
  neighbourhood?: string | null;
  query?: Partial<SearchParams> | null;
}

/** A single headline figure shown in a record's results summary. */
export interface SummaryStat {
  key: 'properties' | 'agencies';
  value: number;
}

/** The parts a search record needs to derive its results summary. */
export interface SearchSummaryInput {
  properties_retrieved?: number | null;
  agencies_found?: number | null;
}

/**
 * Resolves the human-readable location fragment for a search title,
 * preferring the stored geo fields and falling back to the query.
 *
 * @param input - The search record's geo and query fields.
 * @returns The "Neighbourhood, City" (or "City") fragment, or an empty string.
 */
function locationFragment(input: SearchTitleInput): string {
  const city = (input.city || input.query?.city || '').trim();
  const neighbourhood = (input.neighbourhood || input.query?.neighbourhood || '').trim();
  return [neighbourhood, city].filter(Boolean).join(', ');
}

/**
 * Resolves the head noun of the title: the localized property type, or the
 * generic "Imóveis" when no type is known.
 *
 * @param propertyType - The localized property-type label, if any.
 * @returns The head noun.
 */
function headNoun(propertyType?: string): string {
  return propertyType?.trim() || 'Imóveis';
}

/**
 * Appends the optional transaction and location fragments to the head noun.
 *
 * @param head - The resolved head noun.
 * @param transaction - The localized transaction label, if any.
 * @param location - The resolved location fragment, if any.
 * @returns The composed fragments as a single string.
 */
function composeTitle(head: string, transaction: string, location: string): string {
  const parts = [head];
  if (transaction) parts.push(`para ${transaction.toLowerCase()}`);
  if (location) parts.push(`em ${location}`);
  return parts.join(' ').trim();
}

/**
 * Builds a deterministic, auto-generated pt-BR title for a search record.
 *
 * The title reads as a short sentence an agent recognises at a glance:
 * "{Type} para {transaction} em {location}". Each fragment is omitted when the
 * underlying field is absent, so a city-only search still yields a clean title
 * and a fully-specified one yields the richest form. No field is invented and
 * the result is stable for identical inputs. A title that collapses to just
 * the head noun (neither transaction nor location) reads as a category, not a
 * search, so the fallback is used instead.
 *
 * @param input - The search record's geo and query fields.
 * @param labels - Localized labels for the property type and transaction.
 * @param fallback - The label used when no location is known.
 * @returns The composed, non-empty title.
 */
export function buildSearchTitle(
  input: SearchTitleInput,
  labels: { propertyType?: string; transaction?: string } = {},
  fallback = 'Busca de parceria',
): string {
  const location = locationFragment(input);
  const transaction = labels.transaction?.trim() ?? '';
  if (!location && !transaction) return fallback;
  return composeTitle(headNoun(labels.propertyType), transaction, location);
}

/**
 * Whether a reported count should appear in the summary.
 *
 * Properties keep a real zero (finding nothing is a result); agencies only
 * appear when positive (a zero agency count is noise beside the properties).
 *
 * @param value - The reported figure, if any.
 * @param keepZero - Whether a zero value still counts as reportable.
 * @returns True when the figure should be shown.
 */
function isReportable(value: number | null | undefined, keepZero: boolean): boolean {
  if (typeof value !== 'number') return false;
  return keepZero ? value >= 0 : value > 0;
}

/**
 * Builds the ordered "big-numbers" results summary for a search record.
 *
 * Only figures the backend actually reported are included, so a record missing
 * a count shows the counts it has rather than a zero that reads as "found
 * nothing". Properties lead because they are the figure the agent acts on;
 * agencies follow as supporting context.
 *
 * @param input - The record's reported counts.
 * @returns The headline stats in display order.
 */
export function buildSearchSummary(input: SearchSummaryInput): SummaryStat[] {
  const stats: SummaryStat[] = [];
  if (isReportable(input.properties_retrieved, true)) {
    stats.push({ key: 'properties', value: input.properties_retrieved as number });
  }
  if (isReportable(input.agencies_found, false)) {
    stats.push({ key: 'agencies', value: input.agencies_found as number });
  }
  return stats;
}
